/**
 * Template strings for column action components.
 */

// ============================================================================
// Row Actions Component File Template
// ============================================================================

export function generateRowActionsFileTemplate(params: {
  Actions: string
  DeleteDialog: string
  deleteDialogFileName: string
  singular: string
}): string {
  const { Actions, DeleteDialog, deleteDialogFileName, singular } = params

  return `'use client'

import { Button } from '@admin/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@admin/components/ui/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import * as React from 'react'
import { ${DeleteDialog} } from './${deleteDialogFileName}'

export function ${Actions}({ id }: { id: string }) {
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)

  function handleDeleteOpenChange(open: boolean) {
    setDeleteOpen(open)

    if (!open) {
      setMenuOpen(false)
    }
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="size-8 p-0"
          aria-label={\`Open ${singular} actions for \${id}\`}
        >
          <span className="sr-only">Open ${singular} actions</span>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <${DeleteDialog}
          id={id}
          open={deleteOpen}
          onOpenChange={handleDeleteOpenChange}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
`
}

// ============================================================================
// Delete Dialog Component File Template
// ============================================================================

export function generateDeleteDialogFileTemplate(params: {
  Singular: string
  actionImportPath: string
  singular: string
  plural: string
}): string {
  const { Singular, actionImportPath, singular, plural } = params

  return `'use client'

import { delete${Singular} } from '@admin/actions/${actionImportPath}'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@admin/components/ui/alert-dialog'
import { DropdownMenuItem } from '@admin/components/ui/dropdown-menu'
import { useQueryClient } from '@tanstack/react-query'
import { Trash } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'

interface Delete${Singular}DialogProps {
  id: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function Delete${Singular}Dialog({
  id,
  open,
  onOpenChange
}: Delete${Singular}DialogProps) {
  const [isPending, startTransition] = React.useTransition()
  const queryClient = useQueryClient()

  const handleDelete = () => {
    startTransition(async () => {
      try {
        const result = await delete${Singular}(id)

        if (result.success) {
          toast.success('${Singular} deleted successfully')
          queryClient.refetchQueries({ queryKey: ['${plural}'] })
          onOpenChange(false)
        } else {
          toast.error(result.error || 'Failed to delete ${singular}')
        }
      } catch (error) {
        toast.error('An error occurred')
        console.error(error)
      }
    })
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem variant="destructive" onSelect={(event) => event.preventDefault()}>
          <Trash className="size-4" />
          Delete ${singular}
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete this ${singular}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              handleDelete()
            }}
            disabled={isPending}
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
`
}
